import { useGetCourseQuery } from "@/app/services/courseApi";
import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/16/solid";
import {
  QuestionDTO,
  QuestionEvaluationDTOType,
  QuestionQueryDTO,
} from "@onetutor/shared/dto";
import { useEffect, useRef, useState } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router";
import LoadingSpinnerComponent from "../../components/LoadingSpinnerComponent";
import QuizCard from "../../components/QuizCard";
import Header from "../../components/header/Header";
import Modal from "../../components/modal";
import { useGetQuizQuery } from "../../services/quizApi";

export const NO_QUESTIONS_LEFT_MODAL_ID = "noQuestionsLeft";
const QUESTION_FETCH_COUNT = 50;

type Question = QuestionDTO & {
  fetchTimestamp: number;
};

export type QuestionQueryConfig = {
  queryParams: QuestionQueryDTO;
  timestamp: number;
};

export type QuestionsStatus = {
  questionId: string;
  fetchTimestamp: number;
  isChecked: boolean;
  evaluation: QuestionEvaluationDTOType | null;
};

function QuizSessionPage() {
  const { t } = useTranslation(["quizPage", "toast", "coursePage"]);
  const navigate = useNavigate();

  const { courseId } = useParams();
  if (!courseId) {
    throw new Error("No courseId provided");
  }
  const [quizParams] = useSearchParams();
  const topic = quizParams.get("topic");

  const { data: course } = useGetCourseQuery(courseId);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsStatus, setQuestionsStatus] = useState<QuestionsStatus[]>([]);
  const [questionQueryConfig, setQuestionQueryConfig] =
    useState<QuestionQueryConfig>({
      queryParams: {
        topic: topic || undefined,
        count: QUESTION_FETCH_COUNT,
      },
      timestamp: Date.now(),
    });
  const setQueryParams = (queryParams: QuestionQueryDTO) => {
    setQuestionQueryConfig({
      queryParams: queryParams,
      timestamp: Date.now(), // manual trigger for rtk query
    });
  };

  const { data: fetchedQuestions } = useGetQuizQuery({
    courseId,
    questionQueryConfig,
  });

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  const [didIncreaseIndex, setDidIncreaseIndex] = useState(true);
  const questionRefs = useRef<React.RefObject<HTMLDivElement | null>[]>([]);
  const [allQuestionsSubmitted, setAllQuestionsSubmitted] =
    useState<boolean>(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO fix dependencies
  useEffect(() => {
    if (!fetchedQuestions || !questions) {
      return;
    }

    if (fetchedQuestions.questions.length === 0) {
      if (questionQueryConfig.queryParams.startNewSession) {
        navigate(-1);
      }
      setAllQuestionsSubmitted(true);
      if (questions.length === 0) {
        setQueryParams({
          ...questionQueryConfig.queryParams,
          startNewSession: true,
        });
      }
      return;
    }
    setAllQuestionsSubmitted(false);

    const fetchTimestamp = Date.now();

    const questionsWithTimestamp = fetchedQuestions.questions.map(
      (question) => ({
        ...question,
        fetchTimestamp, // timestamp as second identifier so questions can be fetched multiple times
      }),
    );
    setQuestions((prevQuestions) => [
      ...prevQuestions,
      ...questionsWithTimestamp,
    ]);

    questionRefs.current = [
      ...questionRefs.current,
      ...fetchedQuestions.questions.map(() =>
        React.createRef<HTMLDivElement>(),
      ),
    ];

    setQuestionsStatus((prevQuestionsStatus) => {
      const newQuestionsStatus = questionsWithTimestamp.map((question) => ({
        questionId: question.id,
        fetchTimestamp: question.fetchTimestamp,
        isChecked: false,
        evaluation: null,
      }));
      return [...prevQuestionsStatus, ...newQuestionsStatus];
    });
  }, [fetchedQuestions]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: TODO fix dependencies
  useEffect(() => {
    if (currentIndex === questions.length - 1) {
      setQueryParams({
        ...questionQueryConfig.queryParams,
        startNewSession: false,
        excludeIds:
          questionsStatus
            .filter((question) => !question.isChecked)
            .map((question) => question.questionId) || undefined,
      });
    }
  }, [currentIndex, questions]);

  const goToPreviousQuestion = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setDidIncreaseIndex(false);
    }
  };

  const goToNextQuestion = () => {
    if (currentIndex >= 0 && currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setDidIncreaseIndex(true);
    }
  };

  const nextButtonEnabled = () => {
    return questions && currentIndex < questions.length - 1;
  };

  const restartQuiz = () => {
    setQuestions([]);
    setQuestionsStatus([]);
    setCurrentIndex(0);
    setQueryParams({
      ...questionQueryConfig.queryParams,
      excludeIds: undefined,
      startNewSession: true,
    });
  };

  return (
    <Header
      bottomPadding={false}
      breadcrumbs={[
        {
          name:
            course?.name ?? t("breadcrumbFallback.course", { ns: "header" }),
          link: `/course/${courseId}`,
        },
        {
          name: t("quiz"),
          link: `/course/${courseId}/quiz`,
        },
      ]}
    >
      {currentIndex === undefined || questions.length === 0 ? (
        <LoadingSpinnerComponent />
      ) : (
        <div className="mt-2 flex h-[calc(100vh-190px)] flex-col justify-center sm:h-[calc(100vh-150px)]">
          <div className="flex flex-grow items-center overflow-hidden rounded-box">
            <div className="flex h-full w-full flex-col items-center justify-center overflow-y-auto ">
              <QuizCard
                key={questions[currentIndex].id}
                quizData={{
                  question: questions[currentIndex],
                  questionsLength: questions.length,
                  setCurrentIndex: setCurrentIndex,
                  setDidIncreaseIndex: setDidIncreaseIndex,
                  didIncreaseIndex: didIncreaseIndex,
                  isChecked:
                    questionsStatus.find(
                      (checkedQuestion) =>
                        checkedQuestion.questionId ===
                          questions[currentIndex].id &&
                        checkedQuestion.fetchTimestamp ===
                          questions[currentIndex].fetchTimestamp,
                    )?.isChecked || false,
                  setCheckedQuestions: setQuestionsStatus,
                  evaluation:
                    questionsStatus.find(
                      (checkedQuestion) =>
                        checkedQuestion.questionId ===
                          questions[currentIndex].id &&
                        checkedQuestion.fetchTimestamp ===
                          questions[currentIndex].fetchTimestamp,
                    )?.evaluation || null,
                  currentIndex: currentIndex,
                  allQuestionsSubmitted: allQuestionsSubmitted,
                }}
              />
            </div>
          </div>
          <div className={"flex justify-end"}>
            <div className="flex flex-col gap-y-1">
              <button
                disabled={currentIndex <= 0}
                type="button"
                onClick={goToPreviousQuestion}
                className="btn btn-circle bg-primary text-white shadow-lg"
              >
                <ArrowUpIcon className="h-6 w-6" />
              </button>
              <button
                disabled={!nextButtonEnabled()}
                type="button"
                onClick={goToNextQuestion}
                className="btn btn-circle bg-primary text-white shadow-lg"
              >
                <ArrowDownIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      )}
      <Modal
        id={NO_QUESTIONS_LEFT_MODAL_ID}
        title={t("noQuestionsLeft")}
        content={t("finishedQuestionsForTopic")}
        onConfirmLabel={t("restartQuiz")}
        onCancelLabel={t("backToTopics")}
        onConfirm={restartQuiz}
        onCancel={() => {
          navigate(-1);
        }}
      />
    </Header>
  );
}

export default QuizSessionPage;
